import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
const heroVideoUrl = "/videos/38d48007918b11e42b44b8f21e37d4c9_1.mp4";

// Right-side hero collage: /images/hero-section.png supplies the 5 tinted
// pill frames (drawn as the SVG background, at its native 691x568 pixel
// size so the viewBox below matches it 1:1 with no distortion). ONE shared
// video (single element, single crop) is clipped into all 5 pills at once
// — each window naturally reveals a different part of the same frame
// (matching the reference's suitcase/legs/structure/mesh variety) — rather
// than 5 separate video elements each repeating the same crop.
// `outer` is each pill's exact bounding box measured from hero-section.png;
// `inner` is that same pill inset on ONE side only (matching the reference:
// pill 1 and 2 show the image's tinted border on their left edge, the
// center pill 3 shows it on top and bottom, and pills 4 and 5 show it on
// their right edge) so the video sits inside flush against the other edges
// instead of being framed on all four sides.
const HERO_IMAGE_W = 691;
const HERO_IMAGE_H = 568;
const HERO_BORDER = 18;
const heroWindowPills = [
  { key: 1, outer: { x: 0, y: 335, w: 126, h: 196, r: 63 }, inner: { x: 0 + HERO_BORDER, y: 335, w: 126 - HERO_BORDER, h: 196, r: 63 } },
  { key: 2, outer: { x: 141, y: 92, w: 126, h: 439, r: 63 }, inner: { x: 141 + HERO_BORDER, y: 92, w: 126 - HERO_BORDER, h: 439, r: 63 } },
  { key: 3, outer: { x: 282, y: 0, w: 127, h: 568, r: 63.5 }, inner: { x: 282, y: 0 + HERO_BORDER, w: 127, h: 568 - HERO_BORDER * 2, r: 63.5 } },
  { key: 4, outer: { x: 424, y: 73, w: 126, h: 398, r: 63 }, inner: { x: 424, y: 73, w: 126 - HERO_BORDER, h: 398, r: 63 } },
  { key: 5, outer: { x: 565, y: 126, w: 126, h: 246, r: 63 }, inner: { x: 565, y: 126, w: 126 - HERO_BORDER, h: 246, r: 63 } },
];

// CSS clip-path for the pill-window video: the 5 inner pills as rounded
// rects, scaled from hero-section.png's canvas to the rendered size.
const heroPillsClipPath = (scale) => {
  const rect = ({ x, y, w, h, r }) => {
    const [X, Y, W, H] = [x * scale, y * scale, w * scale, h * scale];
    const R = Math.min(r * scale, W / 2, H / 2);
    return `M${X + R},${Y}H${X + W - R}A${R},${R} 0 0 1 ${X + W},${Y + R}V${Y + H - R}A${R},${R} 0 0 1 ${X + W - R},${Y + H}`
      + `H${X + R}A${R},${R} 0 0 1 ${X},${Y + H - R}V${Y + R}A${R},${R} 0 0 1 ${X + R},${Y}Z`;
  };
  return `path('${heroWindowPills.map((pill) => rect(pill.inner)).join('')}')`;
};
import SuccessStoriesSection from '../components/SuccessStoriesSection';
import EdugenFamilySection from '../components/EdugenFamilySection';
import { MastersService as masters } from '../services/masters';
import { CmsService as cms } from '../services/cms';
import { resolveUploadsUrl } from '../utils/resolveUploadsUrl';
import { Helmet } from 'react-helmet-async';
import { loadGoogleReviews } from '../utils/googleReviews';

// Keep in sync with the static fallback in public/index.html, which is what
// crawlers and link previews (WhatsApp, Facebook) read before JS runs.
const HOME_META_DESCRIPTION = 'Edugen is a trusted study abroad consultancy in India offering expert counselling, university admissions, IELTS preparation, and student visa guidance.';

const SHOW_NEWS_BLOGS_SECTION = true;

const CATEGORY_TABS = [
  { name: 'News', icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" /></svg> },
  { name: 'Blogs', icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg> },
  { name: 'Events', icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg> },
  { name: 'Webinars', icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg> },
];

export default function Home() {
  const [activeCategory, setActiveCategory] = useState('News');
  const [categoryTabProgress, setCategoryTabProgress] = useState(0);
  // Tab progress freezes while the pointer is over the News & Blogs section.
  const [newsHovered, setNewsHovered] = useState(false);
  const goToCategory = (direction) => {
    setActiveCategory((prev) => {
      const idx = CATEGORY_TABS.findIndex((c) => c.name === prev);
      const nextIdx = (idx + direction + CATEGORY_TABS.length) % CATEGORY_TABS.length;
      return CATEGORY_TABS[nextIdx].name;
    });
    setCategoryTabProgress(0);
  };
  const [hoveredMarker, setHoveredMarker] = useState(null);
  const markerCloseTimeout = useRef(null);

  const openMarkerCard = (i) => {
    if (markerCloseTimeout.current) {
      clearTimeout(markerCloseTimeout.current);
      markerCloseTimeout.current = null;
    }
    setHoveredMarker(i);
  };

  const scheduleCloseMarkerCard = () => {
    markerCloseTimeout.current = setTimeout(() => {
      setHoveredMarker(null);
    }, 500);
  };
  const [showFullscreenHeroVideo, setShowFullscreenHeroVideo] = useState(false);
  const [collapseHeroSection, setCollapseHeroSection] = useState(false);
  const [fullscreenHeroRevealProgress, setFullscreenHeroRevealProgress] = useState(0);
  const [fullscreenHeroExitProgress, setFullscreenHeroExitProgress] = useState(0);
  const [heroVideoReady, setHeroVideoReady] = useState(false);
  const [heroWindowBounds, setHeroWindowBounds] = useState({ left: 0, top: 0, width: 660, height: 760 });
  const heroWindowRef = useRef(null);
  // const heroVideoUrl = 'https://embed-cloudfront.wistia.com/deliveries/fe3188338c05aafa3ea5846e8f85fc5ac150d5a7.m3u8';
  // const heroVideoUrl = "https://drive.google.com/uc?export=download&id=1yp89x_5fYIDCw12C4nXV8815wOsf93BO";
  const [newsData, setNewsData] = useState({ News: [], Blogs: [], Events: [], Webinars: [] });
  const [newsDataStatus, setNewsDataStatus] = useState('loading'); // 'loading' | 'ready' | 'error'

  const CATEGORY_TO_CMS_TYPE = { News: 'news', Blogs: 'blog', Events: 'event', Webinars: 'webinar' };
  const CATEGORY_TO_LISTING_PATH = { News: '/news', Blogs: '/blogs', Events: '/events', Webinars: '/webinar' };
  const CATEGORY_TO_DETAILS_PATH = { News: '/blog-details', Blogs: '/blog-details', Events: '/event-details', Webinars: '/event-details' };

  useEffect(() => {
    let cancelled = false;

    const formatPostDate = (value) => {
      if (!value) return '';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return String(value);
      return date.toLocaleDateString('en-US', { month: 'long', day: '2-digit', year: 'numeric' });
    };

    (async () => {
      try {
        const categoryNames = Object.keys(CATEGORY_TO_CMS_TYPE);
        const results = await Promise.all(
          categoryNames.map((name) => cms.listPublicPosts(CATEGORY_TO_CMS_TYPE[name], 3))
        );
        if (cancelled) return;

        const grouped = {};
        categoryNames.forEach((name, i) => {
          const rows = Array.isArray(results[i]?.data) ? results[i].data : [];
          grouped[name] = rows.map((row) => ({
            id: row.id,
            tag: row.tag || name.replace(/s$/, ''),
            date: formatPostDate(row.published_date),
            title: row.title,
            image: resolveUploadsUrl(row.cover_image) || '/images/hero-2.webp',
          }));
        });
        setNewsData(grouped);
        setNewsDataStatus('ready');
      } catch (error) {
        console.error('Failed to load news/blogs/events/webinars:', error);
        if (!cancelled) setNewsDataStatus('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // Scroll-snap every section below the hero, so scrolling settles on exactly one
  // section at a time instead of resting mid-way between two. Scoped to this page only
  // (added/removed on mount/unmount) — the hero itself is left out on purpose since it
  // already drives its own scroll-linked video animation.
  useEffect(() => {
    document.documentElement.classList.add('snap-y', 'snap-mandatory');
    return () => {
      document.documentElement.classList.remove('snap-y', 'snap-mandatory');
    };
  }, []);

  useEffect(() => {
    const handleScrollState = () => {
      const viewportHeight = window.innerHeight || 0;
      const scrollTop = window.scrollY || window.pageYOffset || 0;
      const revealEnd = viewportHeight * 0.25; // let fullscreen reach full height at the original pace
      const holdEnd = viewportHeight * 2.2;    // keep fullscreen pinned even on a strong first scroll
      const exitEnd = viewportHeight * 3.4;    // user needs sustained scrolling before content takes over
      const revealStart = 6;

      const isActive = scrollTop > revealStart && scrollTop < exitEnd;
      const shouldCollapseHero = scrollTop > revealStart;
      let revealProgress = 0;
      if (scrollTop > revealStart && scrollTop < revealEnd) {
        revealProgress = (scrollTop - revealStart) / Math.max(1, revealEnd - revealStart);
      } else if (scrollTop >= revealEnd && scrollTop < exitEnd) {
        revealProgress = 1;
      }
      let exitProgress = 0;
      if (scrollTop > holdEnd && scrollTop < exitEnd) {
        exitProgress = (scrollTop - holdEnd) / Math.max(1, exitEnd - holdEnd);
      } else if (scrollTop >= exitEnd) {
        exitProgress = 1;
      }

      setShowFullscreenHeroVideo(isActive);
      setCollapseHeroSection(shouldCollapseHero);
      setFullscreenHeroRevealProgress(Math.max(0, Math.min(1, revealProgress)));
      setFullscreenHeroExitProgress(Math.max(0, Math.min(1, exitProgress)));
    };

    handleScrollState();
    window.addEventListener('scroll', handleScrollState, { passive: true });
    window.addEventListener('resize', handleScrollState);
    return () => {
      window.removeEventListener('scroll', handleScrollState);
      window.removeEventListener('resize', handleScrollState);
    };
  }, []);

  useEffect(() => {
    const updateHeroWindowBounds = () => {
      if (!heroWindowRef.current) return;
      const rect = heroWindowRef.current.getBoundingClientRect();
      setHeroWindowBounds({
        left: rect.left,
        top: rect.top,
        width: rect.width,
        height: rect.height,
      });
    };

    updateHeroWindowBounds();
    window.addEventListener('resize', updateHeroWindowBounds);
    window.addEventListener('scroll', updateHeroWindowBounds, { passive: true });
    return () => {
      window.removeEventListener('resize', updateHeroWindowBounds);
      window.removeEventListener('scroll', updateHeroWindowBounds);
    };
  }, []);

  const viewportWidth = window.innerWidth || 1440;
  const viewportHeight = window.innerHeight || 900;
  // The scroll-into-fullscreen-video effect depends on measuring the pill
  // collage, which only renders at lg+ ("hidden lg:flex" below) — below that
  // breakpoint there's nothing to zoom from, so skip the whole effect and
  // let the hero behave like a normal static section that scrolls away.
  const isDesktopHeroFx = viewportWidth >= 1024;
  const shouldShowLoadedHeroVideo = isDesktopHeroFx && showFullscreenHeroVideo && heroVideoReady;
  const shouldCollapseLoadedHero = isDesktopHeroFx && collapseHeroSection && heroVideoReady;
  const revealEase = 1 - Math.pow(1 - fullscreenHeroRevealProgress, 1.35);
  const fullscreenHeroLeft = heroWindowBounds.left * (1 - revealEase);
  const fullscreenHeroTop = (heroWindowBounds.top * (1 - revealEase)) + (fullscreenHeroExitProgress * -viewportHeight * 1.04);
  const fullscreenHeroWidth = heroWindowBounds.width + ((viewportWidth - heroWindowBounds.width) * revealEase);
  const fullscreenHeroHeight = heroWindowBounds.height + ((viewportHeight - heroWindowBounds.height) * revealEase);
  const fullscreenHeroOpacity = shouldShowLoadedHeroVideo
    ? (fullscreenHeroExitProgress > 0.82
        ? Math.max(0, 1 - ((fullscreenHeroExitProgress - 0.82) / 0.18))
        : 1)
    : 0;
  // Zoom starts from the center pill's exact bounds in the hero-section.png
  // canvas (691x568) rather than the old hand-tuned 660x760 viewBox.
  const heroCenterPillOuter = heroWindowPills[2].outer;
  const fullscreenHeroClipTop = ((heroCenterPillOuter.y / HERO_IMAGE_H) * 100) * (1 - revealEase);
  const fullscreenHeroClipRight = (((HERO_IMAGE_W - (heroCenterPillOuter.x + heroCenterPillOuter.w)) / HERO_IMAGE_W) * 100) * (1 - revealEase);
  const fullscreenHeroClipBottom = (((HERO_IMAGE_H - (heroCenterPillOuter.y + heroCenterPillOuter.h)) / HERO_IMAGE_H) * 100) * (1 - revealEase);
  const fullscreenHeroClipLeft = ((heroCenterPillOuter.x / HERO_IMAGE_W) * 100) * (1 - revealEase);
  const fullscreenHeroClipRadius = heroCenterPillOuter.r * (1 - revealEase);
  // Where the pill artwork is actually drawn inside the hero window (the SVG
  // letterboxes it, preserveAspectRatio "meet"), so the video layer lines up.
  const heroArtScale = Math.min(heroWindowBounds.width / HERO_IMAGE_W, heroWindowBounds.height / HERO_IMAGE_H);
  const heroArtWidth = HERO_IMAGE_W * heroArtScale;
  const heroArtHeight = HERO_IMAGE_H * heroArtScale;
  const heroPillsClip = heroPillsClipPath(heroArtScale);
  const heroSectionOffset = '0px';
  const mainContentOffset = shouldShowLoadedHeroVideo
    ? `${Math.round(Math.max(0, (window.innerHeight || 900) - 520) * (1 - fullscreenHeroExitProgress))}px`
    : '0px';
  const heroSectionOpacity = shouldShowLoadedHeroVideo
    ? (fullscreenHeroRevealProgress < 0.98 ? 1 : 0)
    : (shouldCollapseLoadedHero ? 0 : 1);

  useEffect(() => {
    // The hero video (8+ MB) is only ever visible on desktop (pill column is
    // `hidden lg:flex`, fullscreen effect is gated on isDesktopHeroFx), so
    // mobile never downloads it.
    if (!isDesktopHeroFx) return undefined;
    const videos = Array.from(document.querySelectorAll('[data-hero-video="true"]'));
    if (!videos.length) return undefined;

    const hlsInstances = [];
    let scriptEl = null;
    let startTimer = null;
    const playbackState = { time: 0 };
    setHeroVideoReady(false);

    const markHeroVideoReady = () => {
      setHeroVideoReady(true);
    };

    videos.forEach((video) => {
      video.addEventListener('loadeddata', markHeroVideoReady, { once: true });
      video.addEventListener('canplay', markHeroVideoReady, { once: true });
    });

    const tryPlay = (video) => {
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    };

    const syncVideoTimes = (sourceVideo) => {
      const sourceTime = Number.isFinite(sourceVideo.currentTime) ? sourceVideo.currentTime : playbackState.time;
      playbackState.time = sourceTime;
      videos.forEach((video) => {
        if (video === sourceVideo) return;
        if (video.readyState < 1) return;
        if (Math.abs((video.currentTime || 0) - sourceTime) > 0.18) {
          try {
            video.currentTime = sourceTime;
          } catch (_) {}
        }
      });
    };

    const keepVideoPlaying = (video) => {
      if (video.paused) {
        tryPlay(video);
      }
    };

    const onMasterTimeUpdate = () => {
      syncVideoTimes(videos[0]);
    };

    const onAnyPause = (event) => {
      keepVideoPlaying(event.currentTarget);
      syncVideoTimes(event.currentTarget);
    };

    const onAnyCanPlay = (event) => {
      const currentVideo = event.currentTarget;
      if (currentVideo.readyState >= 1 && Math.abs((currentVideo.currentTime || 0) - playbackState.time) > 0.18) {
        try {
          currentVideo.currentTime = playbackState.time;
        } catch (_) {}
      }
      keepVideoPlaying(currentVideo);
    };

    const onAnyEnded = (event) => {
      const currentVideo = event.currentTarget;
      playbackState.time = 0;
      videos.forEach((video) => {
        if (video.readyState >= 1) {
          try {
            video.currentTime = 0;
          } catch (_) {}
        }
        keepVideoPlaying(video);
      });
      keepVideoPlaying(currentVideo);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      videos.forEach((video) => keepVideoPlaying(video));
      syncVideoTimes(videos[0]);
    };

    videos.forEach((video) => {
      video.addEventListener('timeupdate', onMasterTimeUpdate);
      video.addEventListener('pause', onAnyPause);
      video.addEventListener('canplay', onAnyCanPlay);
      video.addEventListener('playing', onAnyCanPlay);
      video.addEventListener('ended', onAnyEnded);
    });
    document.addEventListener('visibilitychange', onVisibilityChange);

    const attachNative = () => {
      videos.forEach((video) => {
        // React doesn't reliably reflect `muted` to the DOM, and Safari only
        // autoplays videos that are muted and inline.
        video.muted = true;
        video.defaultMuted = true;
        video.setAttribute('muted', '');
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');
        video.src = heroVideoUrl;
        video.load();
        if (video.readyState >= 1) {
          try {
            video.currentTime = playbackState.time;
          } catch (_) {}
        }
        tryPlay(video);
      });
    };

    const setupHls = () => {
      if (!window.Hls || !window.Hls.isSupported()) {
        attachNative();
        return;
      }
      videos.forEach((video) => {
        const hlsInstance = new window.Hls({
          autoStartLoad: true,
          enableWorker: true,
        });
        hlsInstance.loadSource(heroVideoUrl);
        hlsInstance.attachMedia(video);
        hlsInstance.on(window.Hls.Events.MANIFEST_PARSED, () => {
          if (video.readyState >= 1) {
            try {
              video.currentTime = playbackState.time;
            } catch (_) {}
          }
          tryPlay(video);
        });
        hlsInstances.push(hlsInstance);
      });
    };

    const startVideo = () => {
      // hls.js is only needed for .m3u8 streams in browsers without native HLS.
      if (!/\.m3u8(\?|$)/i.test(heroVideoUrl) || videos[0].canPlayType('application/vnd.apple.mpegurl')) {
        attachNative();
      } else if (window.Hls) {
        setupHls();
      } else {
        scriptEl = document.createElement('script');
        scriptEl.src = 'https://cdn.jsdelivr.net/npm/hls.js@latest';
        scriptEl.async = true;
        scriptEl.onload = setupHls;
        document.body.appendChild(scriptEl);
      }
    };

    // Keep the video off the critical path: start it only once the page has
    // finished loading, so it doesn't compete with the hero text/images.
    const onWindowLoad = () => { startTimer = setTimeout(startVideo, 0); };
    if (document.readyState === 'complete') onWindowLoad();
    else window.addEventListener('load', onWindowLoad, { once: true });

    return () => {
      window.removeEventListener('load', onWindowLoad);
      if (startTimer) clearTimeout(startTimer);
      videos.forEach((video) => {
        video.removeEventListener('loadeddata', markHeroVideoReady);
        video.removeEventListener('canplay', markHeroVideoReady);
        video.removeEventListener('timeupdate', onMasterTimeUpdate);
        video.removeEventListener('pause', onAnyPause);
        video.removeEventListener('canplay', onAnyCanPlay);
        video.removeEventListener('playing', onAnyCanPlay);
        video.removeEventListener('ended', onAnyEnded);
      });
      document.removeEventListener('visibilitychange', onVisibilityChange);
      hlsInstances.forEach((instance) => instance.destroy());
      if (scriptEl && scriptEl.parentNode) {
        scriptEl.parentNode.removeChild(scriptEl);
      }
    };
  }, [isDesktopHeroFx]);

  const services = [
    {
      title: "Education Loan Assistance",
      path: "/services/education-loan",
      desc: "Secure funding for your studies.",
      bg: "bg-[#ECF3FE]",
      icon: <img src="/images/icons/Education_Loan_investment-dollar.png" alt="Education Loan" className="w-8 h-8 object-contain" />
    },
    {
      title: "Scholarship Guidance",
      path: "/services/scholarship",
      desc: "Unlock the right scholarships.",
      bg: "bg-[#FDF5F6]",
      icon: <img src="/images/icons/money_scholarship.png" alt="Scholarship" className="w-8 h-8 object-contain" />
    },
    {
      title: "Student Accommodation",
      path: "/contact-us",
      desc: "Find your ideal home abroad.",
      bg: "bg-[#F0F7F4]",
      icon: <img src="/images/icons/service-accommodation.png" alt="Accommodation" className="w-12 h-12 object-contain" />
    },
    {
      title: "Student Visa Assistance",
      path: "/services/visa-service",
      desc: "Get your visa application on track",
      bg: "bg-[#FFF9EB]",
      icon: <img src="/images/icons/service-visa-assistance.png" alt="Visa Assistance" className="w-12 h-12 object-contain" />
    },
    {
      title: "Forex Assistance",
      path: "/services/forex",
      desc: "Transfer funds with ease.",
      bg: "bg-[#F0F0FE]",
      icon: <img src="/images/icons/service-forex.png" alt="Forex" className="w-12 h-12 object-contain" />
    },
    {
      title: "Air Ticket Assistance",
      path: "/services/air-ticket",
      desc: "Book your journey with confidence.",
      bg: "bg-[#FFF1E6]",
      icon: <img src="/images/icons/service-air-tickets.png" alt="Air Tickets" className="w-12 h-12 object-contain" />
    }
  ];

  const [edugenIndex, setEdugenIndex] = useState(0);
  const [reviewsStart, setReviewsStart] = useState(0);
  // Google only hands back 5 reviews, so the row loops endlessly: the list is
  // rendered four times and, after each slide settles outside the middle copy,
  // silently jumps back by one copy's width (reviewsJumping turns the transition
  // off for that one frame) so there's never an end to hit in either direction.
  const [reviewsJumping, setReviewsJumping] = useState(false);
  useEffect(() => {
    if (!reviewsJumping) return undefined;
    let raf2;
    const raf1 = requestAnimationFrame(() => { raf2 = requestAnimationFrame(() => setReviewsJumping(false)); });
    return () => { cancelAnimationFrame(raf1); cancelAnimationFrame(raf2); };
  }, [reviewsJumping]);
  // Track the reviews carousel's real width so on laptop screens (e.g. 13"
  // MacBooks) three cards always fit fully with a sliver of the fourth left
  // peeking out — that sliver is the hover target that slides the row left.
  const [reviewsTrackWidth, setReviewsTrackWidth] = useState(0);
  const reviewsResizeObserver = useRef(null);
  const reviewsTrackRef = useCallback((node) => {
    if (reviewsResizeObserver.current) reviewsResizeObserver.current.disconnect();
    if (!node || typeof ResizeObserver === 'undefined') return;
    reviewsResizeObserver.current = new ResizeObserver(([entry]) => setReviewsTrackWidth(entry.contentRect.width));
    reviewsResizeObserver.current.observe(node);
  }, []);
  const reviewCardWidth = viewportWidth < 640
    ? Math.max(240, Math.min(320, viewportWidth - 80))
    : viewportWidth >= 1024 && reviewsTrackWidth
      ? Math.min(380, Math.floor((reviewsTrackWidth - 96) / 3))
      : 380;
  // How many cards fit fully; the next (partly visible) card is the hover
  // target that slides the row left. 3 on laptops, 4 on wide monitors.
  const reviewsFullCount = reviewsTrackWidth
    ? Math.max(1, Math.floor((reviewsTrackWidth + 24) / (reviewCardWidth + 24)))
    : 3;

  const edugenData = [
    { letter: 'E', title: 'Education', desc: 'Opening doors for your global education dream' },
    { letter: 'D', title: 'Developing', desc: 'Study Abroad Preparation' },
    { letter: 'U', title: 'Uplifting', desc: 'Study Abroad Support' },
    { letter: 'G', title: 'Generation', desc: 'International Students Community' },
    { letter: 'E', title: 'Empowering', desc: 'Study Abroad Counselling' },
    { letter: 'N', title: 'Nurturing', desc: 'End-to-End Student Support' }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setEdugenIndex((prev) => (prev + 1) % edugenData.length);
    }, 2000);
    return () => clearInterval(interval);
  }, [edugenData.length]);

  useEffect(() => {
    if (newsHovered) return undefined;
    const TICK_MS = 40;
    const DURATION_MS = 4000;
    const step = (TICK_MS / DURATION_MS) * 100;
    const interval = setInterval(() => {
      setCategoryTabProgress((prev) => {
        if (prev + step >= 100) {
          setActiveCategory((prevCategory) => {
            const idx = CATEGORY_TABS.findIndex((c) => c.name === prevCategory);
            return CATEGORY_TABS[(idx + 1) % CATEGORY_TABS.length].name;
          });
          return 0;
        }
        return prev + step;
      });
    }, TICK_MS);
    return () => clearInterval(interval);
  }, [newsHovered]);

  const [reviews, setReviews] = useState([]);
  // Hovering the row's edge slides it one card at a time at a calm, fixed pace
  // (one step every REVIEWS_STEP_MS) for as long as the pointer stays there.
  // Previously each card sliding under the cursor re-fired mouseenter, so the
  // row raced through several cards per hover.
  const REVIEWS_STEP_MS = 1800;
  const [reviewsHoverDir, setReviewsHoverDir] = useState(null);
  useEffect(() => {
    if (!reviewsHoverDir) return undefined;
    const n = reviews.length;
    const loops = n > 3;
    const step = () => setReviewsStart((s) => {
      if (reviewsHoverDir === 'right') return loops ? s + 1 : Math.min(s + 1, Math.max(0, n - reviewsFullCount));
      return loops ? s - 1 : Math.max(0, s - 1);
    });
    step();
    const id = setInterval(step, REVIEWS_STEP_MS);
    return () => clearInterval(id);
  }, [reviewsHoverDir, reviews.length, reviewsFullCount]);
  const [reviewSummary, setReviewSummary] = useState({ rating: null, total: null, mapsUrl: null });
  const [reviewsStatus, setReviewsStatus] = useState('loading'); // 'loading' | 'ready' | 'error'


  // The Google Maps JS bundle is heavy and the reviews section is far below
  // the fold, so only load it once the section is about to scroll into view.
  const reviewsSectionRef = useRef(null);
  const [reviewsNearViewport, setReviewsNearViewport] = useState(false);
  useEffect(() => {
    const el = reviewsSectionRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setReviewsNearViewport(true);
      return undefined;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        setReviewsNearViewport(true);
        observer.disconnect();
      }
    }, { rootMargin: '600px 0px' });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!reviewsNearViewport) return undefined;
    let cancelled = false;

    (async () => {
      try {
        const result = await loadGoogleReviews();
        if (cancelled) return;
        setReviews(result.reviews);
        setReviewsStart(result.reviews.length > 3 ? result.reviews.length : 0);
        setReviewSummary({ rating: result.rating, total: result.total, mapsUrl: result.mapsUrl });
        setReviewsStatus(result.reviews.length ? 'ready' : 'error');
      } catch (error) {
        console.error('Failed to load Google reviews:', error);
        if (!cancelled) setReviewsStatus('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [reviewsNearViewport]);


  const renderStars = (filled = 4, total = 5) => (
    Array.from({ length: total }).map((_, s) => (
      s < filled ? (
        <svg key={s} className="w-4 h-4 text-[#F47B5F]" fill="currentColor" viewBox="0 0 20 20">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.953a1 1 0 00.95.69h4.156c.969 0 1.371 1.24.588 1.81l-3.363 2.445a1 1 0 00-.364 1.118l1.286 3.953c.3.922-.755 1.688-1.54 1.118l-3.363-2.445a1 1 0 00-1.176 0l-3.363 2.445c-.784.57-1.84-.196-1.54-1.118l1.286-3.953a1 1 0 00-.364-1.118L2.069 9.38c-.783-.57-.38-1.81.588-1.81h4.156a1 1 0 00.95-.69l1.286-3.953z"/>
        </svg>
      ) : (
        <svg key={s} className="w-4 h-4 text-[#F47B5F]" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 20 20">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.953a1 1 0 00.95.69h4.156c.969 0 1.371 1.24.588 1.81l-3.363 2.445a1 1 0 00-.364 1.118l1.286 3.953c.3.922-.755 1.688-1.54 1.118l-3.363-2.445a1 1 0 00-1.176 0l-3.363 2.445c-.784.57-1.84-.196-1.54-1.118l1.286-3.953a1 1 0 00-.364-1.118L2.069 9.38c-.783-.57-.38-1.81.588-1.81h4.156a1 1 0 00.95-.69l1.286-3.953z"/>
        </svg>
      )
    ))
  );

  const worldMarkers = [
    { top: '35%', left: '18%', country: 'United States of America', code: 'us', align: 'right', href: '/study-in-usa' },
    { top: '18%', left: '21%', country: 'Canada', code: 'ca', align: 'right', href: '/study-in-canada' },
    { top: '26%', left: '45%', country: 'United Kingdom', code: 'gb', align: 'right', href: '/study-in-uk' },
    { top: '43%', left: '59%', country: 'United Arab Emirates', code: 'ae', align: 'left', href: '/study-in-uae' },
    { top: '77%', left: '83%', country: 'Australia', code: 'au', align: 'left', href: '/study-in-australia' },
    { top: '86%', left: '96%', country: 'New Zealand', code: 'nz', align: 'left', href: '/study-in-new-zealand' },
  ];

  const [universitiesByCode, setUniversitiesByCode] = useState({});

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        // Server aggregates unique institutions per country from admission_courses (same
        // source the admin "Programs & Schools" page counts from) and returns only the top 6
        // — never pull the full course list here, it's tens of MB and freezes mobile.
        const res = await masters.getTopInstitutionsByCountry(6);
        const countries = Array.isArray(res?.data) ? res.data : [];

        const normalize = (v) => String(v || '').trim().toLowerCase();
        const byCountryName = new Map(countries.map((c) => [normalize(c?.country_name), c?.institutions || []]));

        const grouped = {};
        worldMarkers.forEach((m) => {
          grouped[m.code] = byCountryName.get(normalize(m.country)) || [];
        });

        if (!cancelled) setUniversitiesByCode(grouped);
      } catch (error) {
        console.error('Failed to load universities for world map:', error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const GoogleGIcon = ({ className }) => (
    <svg className={className} viewBox="0 0 48 48">
      <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"/>
      <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"/>
      <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"/>
      <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"/>
    </svg>
  );

  return (
    <div className="font-sans">
      <Helmet>
        <meta name="description" content={HOME_META_DESCRIPTION} />
        <meta property="og:description" content={HOME_META_DESCRIPTION} />
        <meta property="twitter:description" content={HOME_META_DESCRIPTION} />
      </Helmet>
      <div
        className="pointer-events-none fixed z-[140] overflow-hidden"
        aria-hidden="true"
        style={{
          left: `${fullscreenHeroLeft}px`,
          top: `${fullscreenHeroTop}px`,
          width: `${fullscreenHeroWidth}px`,
          height: `${fullscreenHeroHeight}px`,
          opacity: fullscreenHeroOpacity,
          clipPath: `inset(${fullscreenHeroClipTop}% ${fullscreenHeroClipRight}% ${fullscreenHeroClipBottom}% ${fullscreenHeroClipLeft}% round ${fullscreenHeroClipRadius}px)`,
          willChange: 'left, top, width, height, opacity, clip-path',
        }}
      >
        <video
          data-hero-video="true"
          className="relative z-[1] h-full w-full object-cover object-center"
          muted
          autoPlay
          loop
          playsInline
          preload="auto"
        />
      </div>

      <section
        className="relative h-screen w-screen overflow-hidden m-0 p-0 transition-opacity duration-150"
        style={{
          opacity: heroSectionOpacity,
          marginBottom: heroSectionOffset,
        }}
      >
          <div
            className="absolute inset-0 w-full h-full"
            style={{ background: 'linear-gradient(135deg, #A6F2F3 0%, #FEA6FF 100%)' }}
          />
          <div className="absolute inset-0 flex items-center">
            <div className="grid h-full w-full grid-cols-1 items-center gap-6 px-5 pt-24 sm:px-8 md:px-20 lg:grid-cols-[1fr_1fr] lg:gap-8 lg:pt-16">
            <div className="relative z-10 w-full max-w-[680px] space-y-4 lg:space-y-3 xl:space-y-6 text-left">
              <h1 className="font-['Poppins'] text-4xl sm:text-5xl md:text-[64px] lg:text-[38px] xl:text-[52px] 2xl:text-[64px] font-normal capitalize leading-[1.15] text-black">
                <span className="font-semibold">Elevate</span> Your <br />
                <span className="xl:whitespace-nowrap">Study Abroad Journey</span> <br />
                <span className="normal-case">with</span> <span className="font-semibold">Edugen</span>
              </h1>
              <p className="font-['Inter'] max-w-[572px] text-[20px] lg:text-[16px] xl:text-[20px] font-normal leading-[32px] lg:leading-[24px] xl:leading-[32px] tracking-normal text-[#002C47]">
                Your journey begins with the right guidance. From counselling to admissions, IELTS to visa Edugen is with you at every step.
              </p>
              <div className="flex flex-wrap gap-3 sm:gap-5 pt-4">
                <Link to="/coaching" className="group rounded-full bg-gradient-to-r from-[#e67d00] to-[#078b72] p-[4px] transition-transform hover:-translate-y-0.5">
                  <span className="inline-flex items-center rounded-full bg-white px-4 py-2.5 sm:px-7 sm:py-3.5 font-['Poppins'] text-[16px] font-semibold leading-[100%] tracking-normal text-[#035F6A] whitespace-nowrap">
                    Coaching Classes
                    <svg className="ml-2 sm:ml-3 h-5 w-5 sm:h-6 sm:w-6 transform transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </span>
                </Link>
                <a href="/courses" className="group rounded-full bg-gradient-to-r from-[#e67d00] to-[#078b72] p-[4px] transition-transform hover:-translate-y-0.5">
                  <span className="inline-flex items-center rounded-full bg-white px-4 py-2.5 sm:px-7 sm:py-3.5 font-['Poppins'] text-[16px] font-semibold leading-[100%] tracking-normal text-[#035F6A] whitespace-nowrap">
                    Find your Course
                    <svg className="ml-2 sm:ml-3 h-5 w-5 sm:h-6 sm:w-6 transform transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </span>
                </a>
              </div>
            </div>
            
              {/* Right Column Window Video */}
              <div className={`hidden lg:flex justify-end items-center h-full relative transition-opacity duration-200 ${showFullscreenHeroVideo ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
                <div
                  ref={heroWindowRef}
                  className="relative h-full w-full max-w-[400px] xl:max-w-[660px] origin-bottom-right -translate-x-6 overflow-visible rounded-[36px]"
                >
                  <svg
                    viewBox={`0 0 ${HERO_IMAGE_W} ${HERO_IMAGE_H}`}
                    className="absolute inset-0 h-full w-full overflow-visible"
                    aria-hidden="true"
                  >
                    {/* Pill frames — the actual design asset, not drawn shapes */}
                    <image href="/images/hero-section.png" x="0" y="0" width={HERO_IMAGE_W} height={HERO_IMAGE_H} preserveAspectRatio="none" />

                  </svg>
                  {/* One shared video clipped by all 5 (inset) pill windows at once — each
                      window reveals a different region of the same frame, matching the
                      reference's suitcase/legs/structure/mesh variety. It's a plain HTML
                      layer over the SVG (not a <foreignObject>), because Safari doesn't
                      scale or clip video inside foreignObject. */}
                  <div
                    className="absolute overflow-hidden"
                    style={{
                      left: `${(heroWindowBounds.width - heroArtWidth) / 2}px`,
                      top: `${(heroWindowBounds.height - heroArtHeight) / 2}px`,
                      width: `${heroArtWidth}px`,
                      height: `${heroArtHeight}px`,
                      clipPath: heroPillsClip,
                      WebkitClipPath: heroPillsClip,
                    }}
                  >
                    <video
                      data-hero-video="true"
                      className="h-full w-full object-cover object-center"
                      muted
                      autoPlay
                      loop
                      playsInline
                      preload="auto"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
      </section>

      {/* Main content wrapper with background and z-index to cover fixed video during scroll */}
      <div
        className="relative z-[150] bg-white"
        style={{ marginTop: mainContentOffset }}
      >
        {/* Our Services Section */}
        <section className="min-h-screen flex flex-col justify-center snap-start max-w-7xl mx-auto px-4 sm:px-6 pt-8 md:pt-10 pb-6">
        <div className="text-center mb-8 md:mb-10">
          <h2 className="text-[40px] font-bold text-[#0B1C33] font-['Poppins']">Our Services</h2>
          <p className="mt-4 text-[#4A5568] text-lg font-['Inter']">Complete Support for Your Study Abroad Journey</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 justify-items-center">
          {services.map((service, index) => (
            <Link
              key={index}
              to={service.path}
              className={`group relative rounded-[20px] p-6 flex flex-col items-center text-center transition-all duration-300 hover:bg-[#174035] ${service.bg} h-[172px] w-full max-w-[375px] justify-center gap-4 cursor-pointer shadow-sm hover:shadow-xl`}
            >
              <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center overflow-hidden group-hover:hidden transition-all duration-300 shrink-0 shadow-sm">
                {service.icon}
              </div>
              
              <div className="flex flex-col items-center gap-1 w-full">
                <h3 className="text-[22px] font-bold text-[#262626] font-['Poppins'] group-hover:text-white transition-colors duration-300 leading-tight">
                  {service.title}
                </h3>
                <p className="text-[15px] text-[#262626] font-['Inter'] group-hover:text-gray-200 transition-colors duration-300 leading-snug">
                  {service.desc}
                </p>
              </div>
              
              {/* Button appears on hover replacing the icon */}
              <div className="hidden group-hover:block w-full transition-all duration-300 animate-fadeIn">
                <span className="block px-6 py-2 bg-white text-[#174035] font-bold rounded-full hover:bg-gray-100 transition-colors font-['Poppins'] text-sm w-max mx-auto">
                  Enquire Now
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Who We Are Section */}
      <div className="min-h-[85vh] flex flex-col justify-start xl:justify-center snap-start scroll-mt-24 mx-3 sm:mx-6 md:mx-10 lg:mx-16 mt-6 mb-6 bg-[#F1F4F3] rounded-[40px]">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-8 md:py-10">
        <div className="text-center mb-6">
          <h2 className="text-[24px] sm:text-[30px] md:text-[36px] font-semibold text-[#0E2A46] font-['Poppins'] leading-[1.25] md:leading-[48px] tracking-[-0.5px]">Who We Are?</h2>
          <p className="mt-2 text-[#0E2A46] text-[16px] font-['Inter'] font-normal leading-[24px] tracking-normal xl:whitespace-nowrap">
           We are your guide. We do more than just consult.
          </p>
        </div>

        <div className="bg-white rounded-[20px] px-4 sm:px-8 py-3 mb-5 mt-5 flex flex-col md:flex-row items-center justify-between gap-3 sm:gap-6">
          <div className="flex items-center gap-1.5 sm:gap-2.5 lg:gap-4 text-lg sm:text-2xl lg:text-3xl font-bold tracking-[0.05em] sm:tracking-[0.12em] lg:tracking-[0.2em] uppercase">
            {edugenData.map((item, index) => (
              <span
                key={index}
                className={`transition-colors duration-300 ${index === edugenIndex ? 'text-[#FF5722]' : 'text-[#333]'}`}
              >
                {item.letter}
              </span>
            ))}
          </div>
          <div className="hidden md:block w-px h-8 bg-gray-300"></div>
          <div className="text-[#D1471C] text-xl sm:text-2xl lg:text-3xl font-medium tracking-wide font-['Poppins'] w-full sm:w-[200px] text-center transition-all duration-500">
            {edugenData[edugenIndex].title}
          </div>
          <div className="hidden md:block w-px h-8 bg-gray-300"></div>
          <div className="text-[#4A5568] font-medium text-sm sm:text-lg max-w-md text-center md:text-left font-['Inter'] transition-all duration-500">
            {edugenData[edugenIndex].desc}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-6 lg:gap-10 items-center mb-4">
          <div className="relative flex justify-center lg:justify-start">
            <img loading="lazy" decoding="async"
              src="/images/who_we_are2.jpg"
              alt="About EduGen"
              className="w-full max-w-[220px] h-auto object-cover rounded-lg"
            />
          </div>

          <div className="min-w-0 space-y-2 mt-4 font-['Inter'] text-[#4A5568]">
            <p>
              <span className="font-bold text-[16px] leading-[30px] tracking-[0.5px]">Trusted Study Abroad Consultants in India:</span>{' '}
              <span className="font-normal text-[16px] leading-[25.6px] tracking-normal">we are a trusted name in international education. Since 2018, we've guided students to top universities in Canada, the UK, Australia, and the USA - with a 98% visa success rate.</span>
            </p>
            <p className="font-normal text-[16px] leading-[25.6px] tracking-normal">
              From career counselling and university selection to IELTS coaching and visa support, we make the process smooth and affordable. With offices in Delhi NCR and a global alumni base, Edugen is your trusted bridge to a brighter future.
            </p>

            <div className="pt-4 pb-6">
              <Link to="/about-us" className="inline-flex items-center px-8 py-3 rounded-full bg-gradient-to-r from-[#F68870] to-[#E05C3E] text-white font-bold transition-opacity hover:opacity-90 shadow-lg shadow-orange-200">
                Know More
                <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6 xl:gap-8 pt-5">
          <div className="flex items-center gap-4">
            <img loading="lazy" decoding="async" src="/images/icons/Background.png" alt="" className="w-16 h-16 shrink-0" />
            <div className="min-w-0">
              <div className="text-[24px] font-bold text-[#035F6A] font-['Poppins'] leading-[39px] tracking-normal align-middle capitalize">1500+</div>
              <div className="text-[#4A5568] text-sm font-['Inter']">Students Guided</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <img loading="lazy" decoding="async" src="/images/icons/Background(1).png" alt="" className="w-16 h-16 shrink-0" />
            <div className="min-w-0">
              <div className="text-[24px] font-bold text-[#035F6A] font-['Poppins'] leading-[39px] tracking-normal align-middle capitalize">8+</div>
              <div className="text-[#4A5568] text-sm font-['Inter']">Years of Expertise</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <img loading="lazy" decoding="async" src="/images/icons/Background(3).png" alt="" className="w-16 h-16 shrink-0" />
            <div className="min-w-0">
              <div className="text-[24px] font-bold text-[#035F6A] font-['Poppins'] leading-[39px] tracking-normal align-middle capitalize">120+</div>
              <div className="text-[#4A5568] text-sm font-['Inter']">Global University Partners</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <img loading="lazy" decoding="async" src="/images/icons/Background(4).png" alt="" className="w-16 h-16 shrink-0" />
            <div className="min-w-0">
              <div className="text-[24px] font-bold text-[#035F6A] font-['Poppins'] leading-[39px] tracking-normal align-middle capitalize">98%</div>
              <div className="text-[#4A5568] text-sm leading-tight font-['Inter']">Visa Success Rate</div>
            </div>
          </div>
        </div>
      </section>
      </div>

      {/* Success Stories Section */}
      <div className="min-h-screen flex flex-col justify-center snap-start">
        <SuccessStoriesSection />
      </div>

      {/* Edugen Family Section */}
      <div className="min-h-screen flex flex-col justify-center snap-start">
        <EdugenFamilySection />
      </div>

      {/* Google Reviews Section */}
      <section ref={reviewsSectionRef} className="min-h-screen flex flex-col justify-center snap-start pt-6 pb-6">
        <div className="mx-3 sm:mx-6 md:mx-10 lg:mx-16 bg-[#E7F7F8] rounded-[24px]">
          <div className="px-3 sm:px-6 md:px-10 py-5">
          <div className="text-center mb-4">
            <h2 className="text-[36px] font-semibold leading-[48px] tracking-[-0.5px] text-[#17201D] font-['Poppins']">The Trust we have earned</h2>
            <p className="mt-2 text-[16px] leading-[24px] tracking-normal text-[#414B48] font-['Inter']">Valuable Google Reviews from those who began their journey with Edugen</p>
          </div>
          {reviewsStatus === 'ready' && reviews.length > 0 ? (
            <div
              ref={reviewsTrackRef}
              className="relative w-full overflow-hidden rounded-[24px]"
              onMouseMove={(e) => {
                // Right zone: the partly visible card past the full ones.
                // Left zone: the first ~third of the leftmost card.
                const x = e.clientX - e.currentTarget.getBoundingClientRect().left;
                const dir = x > reviewsFullCount * (reviewCardWidth + 24) - 12
                  ? 'right'
                  : x < reviewCardWidth * 0.35 ? 'left' : null;
                setReviewsHoverDir(dir);
              }}
              onMouseLeave={() => setReviewsHoverDir(null)}
            >
              <div
                className={`flex gap-6 ${reviewsJumping ? '' : 'transition-transform duration-[1000ms] ease-in-out'}`}
                style={{ transform: `translateX(-${reviewsStart * (reviewCardWidth + 24)}px)` }}
                onTransitionEnd={(e) => {
                  const n = reviews.length;
                  if (e.target !== e.currentTarget || n <= 3) return;
                  if (reviewsStart < n || reviewsStart >= 2 * n) {
                    setReviewsJumping(true);
                    setReviewsStart((s) => (((s % n) + n) % n) + n);
                  }
                }}
              >
                {(reviews.length > 3 ? [...reviews, ...reviews, ...reviews, ...reviews] : reviews).map((rv, i) => (
                  <div
                    key={i}
                    style={{ width: `${reviewCardWidth}px` }}
                    className="shrink-0 h-[340px] flex flex-col bg-white rounded-[16px] border-2 border-[#E6E6E6] p-5"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-14 h-14 rounded-full overflow-hidden bg-gray-100 flex items-center justify-center">
                        {rv.img ? (
                          <img loading="lazy" decoding="async"
                            src={rv.img}
                            alt={rv.name}
                            referrerPolicy="no-referrer"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                              e.currentTarget.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <span
                          className={`${rv.img ? 'hidden' : 'flex'} w-full h-full items-center justify-center text-lg font-semibold text-gray-400`}
                        >
                          {(rv.name || '?').trim().charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <div className="font-['Poppins'] font-semibold text-[16px] leading-[30px] tracking-normal text-black">{rv.name}</div>
                        <div className="flex items-center gap-1 text-sm">
                          {renderStars(rv.rating)}
                        </div>
                      </div>
                    </div>
                    <div className="mt-2 text-[#4A5568] text-base">
                      <span className="font-semibold">{rv.rating}</span> Rating
                    </div>
                    <div className="mt-2 border-t border-[#E6E6E6] pt-2 flex-1 min-h-0 overflow-hidden">
                      <div className="font-['Inter'] font-bold text-[14px] leading-[24px] tracking-normal text-[#17201D]">Reviewed on {rv.date}</div>
                      <p className="mt-1 font-['Inter'] font-normal text-[14px] leading-[24px] tracking-normal text-[#414B48] line-clamp-3">{rv.text}</p>
                    </div>
                    <div className="mt-3 flex items-center gap-2">
                      <button
                        type="button"
                        className="inline-flex items-center justify-center w-[67px] h-8 rounded-full bg-[#F47B5F] text-white font-['Poppins'] font-medium text-[12px] leading-[16px] align-middle px-3 py-2"
                      >
                        Helpful
                      </button>
                      <button
                        type="button"
                        className="inline-flex items-center justify-center w-[67px] h-8 rounded-full bg-[#F47B5F] text-white font-['Poppins'] font-medium text-[12px] leading-[16px] align-middle px-3 py-2"
                      >
                        Reply
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="py-10 text-center text-[#4A5568]">
              {reviewsStatus === 'loading' ? 'Loading Google reviews…' : 'Reviews are unavailable right now.'}
            </div>
          )}
          <div className="mt-4 bg-white rounded-[16px] px-4 sm:px-6 py-4 sm:py-3 flex flex-col sm:flex-row items-center sm:justify-between gap-3">
            <div className="flex flex-wrap items-center justify-center sm:justify-start gap-x-3 gap-y-1.5">
              <GoogleGIcon className="w-8 h-8 sm:w-9 sm:h-9" />
              <span className="text-[#17201D] font-semibold text-[24px] leading-[32px] tracking-normal font-['Poppins'] whitespace-nowrap">Google Reviews</span>
              <span className="text-[#F47B5F] font-bold text-lg sm:text-xl">
                {reviewSummary.rating != null ? reviewSummary.rating.toFixed(1) : '--'}
              </span>
              <div className="flex items-center gap-0.5">
                {renderStars(Math.round(reviewSummary.rating || 0))}
              </div>
              {reviewSummary.total != null ? (
                <span className="text-sm text-gray-500">({reviewSummary.total.toLocaleString()})</span>
              ) : null}
              <span className="text-xs text-gray-500">Powered by Google</span>
            </div>
            <a
              href={reviewSummary.mapsUrl || 'https://www.google.com/maps/search/Edugen+Study+Abroad+Consultants'}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-[147px] h-8 inline-flex items-center justify-center gap-2 rounded-[50px] bg-gradient-to-r from-[#F68870] to-[#E05C3E] text-white font-semibold text-xs transition-opacity hover:opacity-90"
            >
              View All Reviews
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </a>
          </div>
          </div>
        </div>
      </section>

      {/* Worldwide Universities Section */}
      <section className="min-h-screen flex flex-col justify-start xl:justify-center snap-start pt-24 xl:pt-6 pb-6">
        <div className="mx-3 sm:mx-6 md:mx-10 lg:mx-16 bg-[#F7F7F7] rounded-[40px] relative pb-12">
          <div className="pt-10 px-6 text-center">
            <h2 className="text-[36px] font-semibold leading-[48px] tracking-[-0.5px] text-[#17201D] font-['Poppins']">Worldwide Universities</h2>
            <p className="mt-2 text-[16px] leading-[24px] tracking-normal text-[#414B48] font-['Inter']">Trusted global partnerships that turn study-abroad dreams into real destinations.</p>
          </div>

          <div className="relative mt-5 w-[88%] sm:w-[72%] md:w-[64%] xl:w-[58%] mx-auto">
            <img loading="lazy" decoding="async" src="/images/world_map.png" alt="World Map" className="w-full h-auto object-contain mx-auto" />
            
            {/* Markers */}
            {worldMarkers.map((m, i) => (
              <div key={i} className={`absolute ${hoveredMarker === i ? 'z-40' : 'z-20'}`} style={{ top: m.top, left: m.left }}>
                <div className="relative -translate-x-1/2 -translate-y-full">
                  <button
                    type="button"
                    className="flex flex-col items-center cursor-pointer"
                    onMouseEnter={() => openMarkerCard(i)}
                    onMouseLeave={scheduleCloseMarkerCard}
                    onFocus={() => openMarkerCard(i)}
                  >
                    <span className="w-8 h-8 rounded-full overflow-hidden border-2 border-white shadow-lg ring-1 ring-black/10 bg-white">
                      <img loading="lazy" decoding="async"
                        src={`https://flagcdn.com/w80/${m.code}.png`}
                        alt={m.country}
                        className="w-full h-full object-cover"
                      />
                    </span>
                    <span className="w-2.5 h-2.5 -mt-1 rotate-45 bg-white border-r-2 border-b-2 border-white shadow-sm" style={{ boxShadow: '1px 1px 2px rgba(0,0,0,0.15)' }} />
                  </button>

                  {hoveredMarker === i && (
                  <div
                    onMouseEnter={() => openMarkerCard(i)}
                    onMouseLeave={scheduleCloseMarkerCard}
                    className={`absolute top-0 z-30 w-[min(280px,70vw)] bg-white rounded-2xl shadow-2xl border border-gray-100 p-5 text-left ${
                      m.align === 'left' ? 'right-full mr-2' : 'left-full ml-2'
                    }`}
                  >
                    <div className="flex items-center gap-2 pb-3 mb-3 border-b border-gray-100">
                      <img loading="lazy" decoding="async"
                        src={`https://flagcdn.com/w40/${m.code}.png`}
                        alt={m.country}
                        className="w-7 h-5 rounded-sm object-cover border border-gray-100 shrink-0"
                      />
                      <span className="font-semibold text-[#0B1C33] text-[15px]">{m.country}</span>
                    </div>

                    <div className="text-[11px] font-bold tracking-wider text-gray-400 mb-3">TOP UNIVERSITIES</div>

                    {(universitiesByCode[m.code] || []).length > 0 ? (
                      <div className="grid grid-cols-3 gap-3 mb-4">
                        {(universitiesByCode[m.code] || []).map((uni) => (
                          <Link
                            key={uni.id}
                            to={`/universities/${uni.id}`}
                            className="text-center group"
                          >
                            <div className="h-10 rounded-lg bg-gray-50 border border-gray-100 flex items-center justify-center p-1 mb-1 group-hover:border-[#FF5722] transition-colors">
                              {resolveUploadsUrl(uni.institution_logo) ? (
                                <img loading="lazy" decoding="async"
                                  src={resolveUploadsUrl(uni.institution_logo)}
                                  alt={uni.name}
                                  className="max-h-full max-w-full object-contain"
                                  onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                    e.currentTarget.nextSibling.style.display = 'flex';
                                  }}
                                />
                              ) : null}
                              <span
                                className={`${resolveUploadsUrl(uni.institution_logo) ? 'hidden' : 'flex'} w-full h-full items-center justify-center text-[9px] font-semibold text-gray-400`}
                              >
                                LOGO
                              </span>
                            </div>
                            <div className="text-[10px] text-gray-600 leading-tight group-hover:text-[#FF5722] transition-colors">{uni.name}</div>
                          </Link>
                        ))}
                      </div>
                    ) : (
                      <div className="mb-4 text-[12px] text-gray-400 text-center py-2">Universities coming soon</div>
                    )}

                    {/* Opens the country's "Study in <country>" page. */}
                    <Link
                      to={m.href}
                      className="w-full flex items-center justify-center gap-2 py-2.5 rounded-full border border-[#FF5722] text-[#D1471C] font-semibold text-sm hover:bg-orange-50 transition-colors"
                    >
                      Explore More
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                      </svg>
                    </Link>
                  </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Stats Bar */}
          <div className="relative mt-5 w-full px-6">
            <div className="bg-white rounded-2xl shadow-[0px_0px_20px_0px_#0000001A] py-6 px-6 md:px-8 xl:py-3 xl:px-12 grid grid-cols-1 sm:grid-cols-2 xl:flex xl:flex-row justify-between items-center gap-6 xl:gap-8">
              <div className="flex items-center gap-3">
                <span className="font-['Poppins'] font-medium text-[32px] leading-[39px] tracking-normal align-middle capitalize text-[#DF7157]">100+</span>
                <span className="font-['Inter'] font-normal text-[16px] leading-[17px] tracking-normal align-middle capitalize text-black">Universities</span>
              </div>
              <div className="hidden xl:block w-px h-6 bg-gray-200"></div>
              <div className="flex items-center gap-3">
                <span className="font-['Poppins'] font-medium text-[32px] leading-[39px] tracking-normal align-middle capitalize text-[#DF7157]">10+</span>
                <span className="font-['Inter'] font-normal text-[16px] leading-[17px] tracking-normal align-middle capitalize text-black">Countries</span>
              </div>
              <div className="hidden xl:block w-px h-6 bg-gray-200"></div>
              <div className="flex items-center gap-3">
                <span className="font-['Poppins'] font-medium text-[32px] leading-[39px] tracking-normal align-middle capitalize text-[#DF7157]">999+</span>
                <span className="font-['Inter'] font-normal text-[16px] leading-[17px] tracking-normal align-middle capitalize text-black">Courses</span>
              </div>
              <div className="hidden xl:block w-px h-6 bg-gray-200"></div>
              <div className="flex items-center gap-3">
                <span className="font-['Poppins'] font-medium text-[32px] leading-[39px] tracking-normal align-middle capitalize text-[#DF7157]">100%</span>
                <span className="font-['Inter'] font-normal text-[16px] leading-[17px] tracking-normal align-middle capitalize text-black">Satisfied Students</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Latest News & Blogs Section */}
      {SHOW_NEWS_BLOGS_SECTION && (
      <section className="min-h-screen flex flex-col justify-center snap-start pt-6 pb-6">
        <div
          className="mx-3 sm:mx-6 md:mx-10 lg:mx-16 bg-[#FFF9EB] rounded-[40px] px-3 sm:px-6 py-6 relative"
          onMouseEnter={() => setNewsHovered(true)}
          onMouseLeave={() => setNewsHovered(false)}
        >

          {/* Top Tabs */}
          <div className="flex flex-wrap justify-center gap-4 mb-5">
            {CATEGORY_TABS.map((category, index) => {
              const activeIndex = CATEGORY_TABS.findIndex((c) => c.name === activeCategory);
              const isPast = index < activeIndex;
              const isCurrent = index === activeIndex;
              const fill = isPast ? 100 : isCurrent ? categoryTabProgress : 0;
              return (
                <button
                  key={category.name}
                  onClick={() => {
                    setActiveCategory(category.name);
                    setCategoryTabProgress(0);
                  }}
                  className="relative w-[150px] sm:w-[190px] md:w-[230px] lg:w-[190px] xl:w-[250px] min-[1480px]:w-[306px] h-12 rounded-[40px] font-['Poppins'] font-medium text-[13px] sm:text-[14px] lg:text-[16px] leading-[24px] tracking-normal border border-[#F47B5F] overflow-hidden bg-white"
                >
                  {/* Base layer (orange, unfilled state) */}
                  <span className="relative z-0 h-full flex items-center justify-center gap-[8px] sm:gap-[16px] lg:gap-[24px] text-[#C2410C]">
                    {category.icon}{category.name}
                  </span>

                  {/* Black fill sweeping left to right */}
                  <span
                    className="absolute inset-y-0 left-0 bg-black z-[1]"
                    style={{ width: `${fill}%`, transition: isCurrent ? 'width 40ms linear' : 'none' }}
                  />

                  {/* White text revealed only over the black fill */}
                  <span
                    className="absolute inset-y-0 left-0 z-[2] overflow-hidden"
                    style={{ width: `${fill}%`, transition: isCurrent ? 'width 40ms linear' : 'none' }}
                  >
                    <span className="w-[150px] sm:w-[190px] md:w-[230px] lg:w-[190px] xl:w-[250px] min-[1480px]:w-[306px] h-full flex items-center justify-center gap-[8px] sm:gap-[16px] lg:gap-[24px] text-white absolute inset-y-0 left-0">
                      {category.icon}{category.name}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>

          {/* Heading */}
          <div className="text-center mb-6">
            <h2 className="text-[36px] font-semibold leading-[48px] tracking-[-0.5px] text-black font-['Poppins']">
              Latest From the <span className="text-[#DF7157]">News &amp; Blogs</span>
            </h2>
          </div>

          {/* Cards Carousel */}
          <div className="relative">
            {/* Left Arrow */}
            <button
              type="button"
              onClick={() => goToCategory(-1)}
              aria-label="Previous category"
              className="hidden sm:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 w-12 h-12 rounded-full border-2 border-[#F47B5F] items-center justify-center bg-white hover:bg-orange-50 z-10"
            >
              <img loading="lazy" decoding="async" src="/images/icons/left-arrow.png" alt="Previous" className="w-5 h-5" />
            </button>

            {/* Right Arrow */}
            <button
              type="button"
              onClick={() => goToCategory(1)}
              aria-label="Next category"
              className="hidden sm:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 w-12 h-12 rounded-full border-2 border-[#F47B5F] items-center justify-center bg-white hover:bg-orange-50 z-10"
            >
              <img loading="lazy" decoding="async" src="/images/icons/Right-arrow(1).png" alt="Next" className="w-5 h-5" />
            </button>

            {newsDataStatus === 'ready' && (newsData[activeCategory] || []).length === 0 ? (
              <div className="py-12 text-center text-[#4A5568]">No {activeCategory.toLowerCase()} published yet.</div>
            ) : newsDataStatus === 'error' ? (
              <div className="py-12 text-center text-[#4A5568]">Unable to load {activeCategory.toLowerCase()} right now.</div>
            ) : (
              <div className="flex flex-wrap justify-center gap-6 px-0 sm:px-10 md:px-12">
                {(newsData[activeCategory] || []).map((item) => (
                  <div key={item.id} className="w-full sm:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] flex flex-col bg-white rounded-[24px] overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                    <div className="h-[214px] rounded-t-[24px] overflow-hidden bg-[#D9D9D9]">
                      <img loading="lazy" decoding="async" src={item.image} alt={item.title} className="w-full h-full object-cover" />
                    </div>
                    <div className="p-4 pt-3 flex flex-col flex-1">
                      <div className="flex items-center gap-2 font-['Poppins'] font-medium text-[14px] leading-[14px] tracking-[0.3px] text-[#707070] mb-2">
                        <span>{item.tag}</span>
                        <span className="text-[#F47B5F]">•</span>
                        <span>{item.date}</span>
                      </div>
                      <h3 className="font-['Poppins'] font-medium text-[20px] leading-[28px] tracking-[0.3px] text-[#121212] mb-3 line-clamp-2">
                        {item.title}
                      </h3>
                      <Link
                        to={`${CATEGORY_TO_DETAILS_PATH[activeCategory]}/${item.id}`}
                        className="self-start mt-auto inline-flex items-center px-6 py-2 rounded-full border border-[#F47B5F] text-[#C2410C] font-['Poppins'] font-medium text-[14px] leading-none align-middle tracking-normal hover:bg-orange-50 transition-colors"
                      >
                        Read More <span className="ml-2">→</span>
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Bottom Button */}
          <div className="text-center mt-6">
            <Link
              to={CATEGORY_TO_LISTING_PATH[activeCategory]}
              className="w-[144px] h-12 inline-flex items-center justify-center rounded-[50px] bg-gradient-to-r from-[#F68870] to-[#E05C3E] text-white font-bold transition-opacity hover:opacity-90 shadow-lg"
            >
              Read More <span className="ml-2">→</span>
            </Link>
          </div>

        </div>
      </section>
      )}

      <section className="min-h-screen flex flex-col justify-center snap-start pt-6 pb-12">
        <div className="mx-3 sm:mx-6 md:mx-10 lg:mx-16 rounded-[40px] overflow-hidden relative bg-center bg-cover" style={{ backgroundImage: "url('/images/app-section-bg.jpg')" }}>
          <div
            className="absolute inset-0"
            style={{ background: 'linear-gradient(120deg, #65D6FF 0%, #FF90CD 100%)', opacity: 0.8 }}
          />
          <div className="relative z-10 max-w-7xl mx-auto px-6 pt-8 pb-16 md:pt-12 md:pb-20 grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div className="min-w-0">
              <div className="space-y-2">
                <p className="text-[#17201D] font-['Poppins'] font-semibold text-[22px] sm:text-[36px] md:text-[48px] leading-[100%] tracking-[0.25px]">Our Interactive</p>
                <p className="text-[#17201D] font-['Poppins'] font-medium text-[32px] sm:text-[56px] md:text-[80px] leading-[1.1] md:leading-[88px] tracking-[-0.5px]">Mobile App</p>
                <p className="text-[#18191F] font-['Poppins'] font-light text-[20px] sm:text-[36px] md:text-[48px] leading-[100%] tracking-[-1.5px]">Coming soon</p>
              </div>
              <div className="mt-6 flex flex-wrap items-center gap-3 sm:gap-4">
                <span className="w-[120px] h-10 rounded-[6px] border border-black bg-white overflow-hidden flex items-center justify-center shrink-0">
                  <img loading="lazy" decoding="async" src="/images/googleplaybadge.png" alt="Get it on Google Play" className="w-full h-full object-contain" />
                </span>
                <span className="w-[120px] h-10 rounded-[6px] border border-black bg-white overflow-hidden flex items-center justify-center shrink-0">
                  <img loading="lazy" decoding="async" src="/images/appstorebadge.png" alt="Download on the App Store" className="w-full h-full object-contain" />
                </span>
              </div>
              <div className="mt-8 w-full max-w-[330px] h-12 bg-white rounded-[50px] shadow flex items-center gap-1 overflow-hidden">
                <input type="email" placeholder="Enter your email to get notified" className="min-w-0 flex-1 h-full pl-3 sm:pl-4 pr-1 text-sm sm:text-base outline-none border-none text-[#0B1C33]" />
                <button className="shrink-0 w-[124px] h-12 rounded-[50px] text-sm sm:text-base bg-gradient-to-r from-[#F68870] to-[#E05C3E] text-white font-bold">Notify Me</button>
              </div>
            </div>
            <div className="relative flex justify-center md:justify-end">
              <div className="w-[160px] h-[160px] sm:w-[240px] sm:h-[240px] md:w-[360px] md:h-[360px] lg:w-[440px] lg:h-[440px] rounded-full bg-white/50 backdrop-blur-sm overflow-hidden shadow-[0_10px_40px_rgba(0,0,0,0.15)]">
                <img loading="lazy" decoding="async" src="/images/mob-app.png" alt="Edugen Mobile App" className="w-full h-full object-contain" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main content wrapper ends */}
      </div>

    </div>
  );
}
